import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchStrandedInventory,
  imogenSelector,
} from "../../../../../redux/slices/imogen";

const STALENESS_HOURS = 48;

const formatSourceTimestamp = (iso) => {
  if (!iso) return "unknown";
  return moment.utc(iso).format("MMM D, YYYY");
};

const isStale = (iso) => {
  if (!iso) return true;
  return moment.utc().diff(moment.utc(iso), "hours") > STALENESS_HOURS;
};

const StrandedInventory = ({ store }) => {
  const dispatch = useDispatch();
  const {
    strandedInventory: data,
    strandedInventoryLoading: loading,
    strandedInventoryErrorMessage: errorMessage,
  } = useSelector(imogenSelector);

  useEffect(() => {
    dispatch(fetchStrandedInventory({ store }));
  }, [dispatch, store]);

  const sourceFreshness = useMemo(() => {
    if (!data?.data_freshness) return null;
    const ts = data.data_freshness.fba_stranded_inventory_latest;
    return { timestamp: ts, stale: isStale(ts) };
  }, [data]);

  const sortedItems = useMemo(() => {
    if (!data?.items) return [];
    return [...data.items].sort(
      (a, b) => (b.days_stranded ?? 0) - (a.days_stranded ?? 0)
    );
  }, [data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage) {
    return (
      <Alert severity="error">
        Failed to load stranded inventory: {errorMessage}
      </Alert>
    );
  }

  if (!data) return null;

  const lastRefresh = data.computed_at
    ? moment.utc(data.computed_at).format("MMM D, HH:mm [UTC]")
    : null;

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        mb={1}
      >
        <Box>
          <Typography variant="h6">
            Stranded inventory
            {sortedItems.length > 0 ? ` (${sortedItems.length})` : ""}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            FBA units flagged as stranded by Amazon across all accounts. Sorted
            by days stranded.
          </Typography>
        </Box>
        {lastRefresh && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastRefresh}
          </Typography>
        )}
      </Box>

      {sourceFreshness && (
        <Typography
          variant="caption"
          color={sourceFreshness.stale ? "warning.main" : "text.secondary"}
          sx={{ display: "block", mb: 2 }}
        >
          FBA stranded inventory last updated{" "}
          {formatSourceTimestamp(sourceFreshness.timestamp)}
          {sourceFreshness.stale && " — data may be stale"}
        </Typography>
      )}

      {sortedItems.length === 0 ? (
        <Box p={3}>
          <Typography variant="body2" color="text.secondary">
            No stranded inventory across active stores.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>MP</TableCell>
                <TableCell>ASIN</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Product name</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Days stranded</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((item) => (
                <TableRow key={item.item_key}>
                  <TableCell>{item.store}</TableCell>
                  <TableCell>{item.marketplace}</TableCell>
                  <TableCell>{item.asin}</TableCell>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>{item.product_name}</TableCell>
                  <TableCell align="right">
                    {item.fulfillable_quantity ?? "—"}
                  </TableCell>
                  <TableCell>{item.stranded_reason ?? "—"}</TableCell>
                  <TableCell align="right">
                    {item.days_stranded ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default StrandedInventory;
